import React, { useMemo } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { Button } from '@demandlocal/components'
import { capitalize, isEmpty } from 'lodash'
import { useGetThirdPartyVendors } from '../../../../api/utils/useGetThirdPartyVendors'
import { VENDOR } from '../../../../lib/utils/vendors'
import { getImageURL } from '../../../../lib/utils/getImageURL'
import { ReactComponent as ChevronLeft } from '../../../../images/icons/chevron_left.svg'
import { ReactComponent as PlusIcon } from '../../../../images/icons/plus.svg'
import { ReactComponent as LinkIcon } from '../../../../images/icons/connect_link_icon.svg'
import { match } from 'ts-pattern'
import SourceCDK from '../../Sources/CDK'
import SourceDealerSocket from '../../Sources/DealerSocket'
import SourceDealerVault from '../../Sources/DealerVault'
import { useModal } from '../../../../components/useModal'
import SourceDriveCentric from '../../Sources/DriveCentric'
import SourceElead from '../../Sources/Elead'
import FacebookLeadsFileMapper from '../../UploadCustomers/3rdParty/FacebookLeadsFileMapper'
import { useGetSaasCustomersColumns } from '../../../../api/dealership/customers/util/useGetSaasCustomersColumns'
import { openSignInWindow } from '../../../../lib/utils/openSigninWindow'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import SourceFacebookLeads from '../../Sources/FacebookLeads'
import SourceGoogleLeads from '../../Sources/GoogleLeads'
import GoogleSpreadsheetsFileMapper from '../../UploadCustomers/3rdParty/GoogleSpreadsheetsFileMapper'
import SourceGoogleSpreadSheets from '../../Sources/GoogleSpreadSheets'
import SourceTekion from '../../Sources/Tekion'
import SourceVinSolutions from '../../Sources/VinSolutions'
import { useCreateThirdPartySource } from '../../../../api/dealership/customers/sources/useCreateThirdPartySource'
import { EleadInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceElead'
import { DealerSocketInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceDealerSocket'
import { CDKInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceCDKFiSales'
import { VinSolutionsInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceVinSolutions'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { GA4 } from '../../../../lib/utils/ga4'
import mtm from '../../../../lib/utils/mtm'
import SourceProcede from '../../Sources/Procede'
import { currentUserSelector } from '../../../../store/selectors/userSelector'
import { useSelector } from 'react-redux'
import SourcePromax from '../../Sources/Promax'
import SourceHubSpot from '../../Sources/HubSpot'
import SourceLaunchLabs from '../../Sources/LaunchLabs'
import LaunchLabsConnectInstructions from '../../../../components/ThirdParty/LaunchLabsConnect'
import { TekionInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceTekion'
import { DriveCentricInstructions } from '../../../../components/ThirdParty/ConnectedSource/ConnectedSourceDriveCentric'

type FieldValues = {
  vendors: Array<{
    fetch_timezone?: string
    fetch_time?: { value: string }
    fetch_day?: { value: string }
    fetch_frequency?: { value: string }
    source_name?: string
    vendor_id: number
  }>
}

export default function ThirdPartyConnectSource({
  sourceType,
  onSuccess,
}: {
  sourceType: string
  onSuccess: () => void
}): JSX.Element {
  const { data: vendors, isLoading } = useGetThirdPartyVendors()
  const formContext = useForm<FieldValues>({
    mode: 'onSubmit',
  })

  const { mutateAsync } = useCreateThirdPartySource()
  const user = useSelector(currentUserSelector)

  const { data: databaseColumns } = useGetSaasCustomersColumns()
  const { closeModal, handleModal } = useModal()
  const dealership = useDealership()
  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext

  const {
    fields,
    append,
    remove: removeVendor,
  } = useFieldArray({
    control: formContext.control,
    name: 'vendors',
  })

  const formValues = watch('vendors')
  const hasVendorSelected = formValues?.length > 0

  const mappedVendorsToSourceType = useMemo(() => {
    if (isLoading) return []
    return [
      {
        sourceType: 'leads',
        disabled: sourceType !== 'leads',
        vendors: [
          VENDOR.Elead,
          VENDOR.FacebookLeads,
          VENDOR.GoogleLeads,
          VENDOR.GoogleSpreadSheets,
          VENDOR.VinSolutions,
          VENDOR.Tekion,
          VENDOR.DealerSocket,
          VENDOR.Promax,
          VENDOR.HubSpot,
          VENDOR.LaunchLabs,
        ],
      },
      {
        sourceType: 'sales',
        disabled: sourceType !== 'sales',
        vendors: [
          VENDOR.CDK,
          VENDOR.DealerSocket,
          VENDOR.GoogleSpreadSheets,
          VENDOR.DealerVault,
          VENDOR.Tekion,
          VENDOR.DriveCentric,
          VENDOR.Procede,
        ],
      },
      {
        sourceType: 'service',
        disabled: sourceType !== 'service',
        vendors: [
          VENDOR.CDK,
          VENDOR.DealerSocket,
          VENDOR.GoogleSpreadSheets,
          VENDOR.Tekion,
          VENDOR.DealerVault,
          VENDOR.Procede,
        ],
      },
      {
        sourceType: 'other',
        disabled: sourceType !== 'other',
        vendors: [VENDOR.GoogleSpreadSheets],
      },
    ].map((source) => {
      return {
        ...source,
        vendors: source.vendors.map((vendor) => {
          return vendors?.find((v) => v.id === vendor)
        }),
      }
    })
  }, [sourceType, vendors, isLoading])

  const translateSourceTypeToNumber = useMemo(() => {
    switch (sourceType) {
      case 'leads':
        return 1
      case 'sales':
        return 2
      case 'service':
        return 3
      case 'other':
        return 4
      default:
        return 4
    }
  }, [sourceType])

  type Payload = {
    vendor_id: number
    sourceName?: string
    sourceType: number
    schedule_data: {
      fetch_frequency?: string
      fetch_day?: string
      fetch_time?: string
      fetch_timezone?: string
    }
    config_data?: {
      cdk_api_type?: string
      dealer_id?: string
      date_from?: string
      file_type?: string
      dv_dealer_id?: string
      dv_file_type?: string
      product?: string
      dealer_vault_setup_info?: Record<string, unknown>
    }
    accounts?: Array<{ account_id: string; rowsMapping: Record<string, unknown> }>
    spreadsheets?: Array<{
      spreadsheet_id: string
      rowsMapping: Record<string, unknown>
      last_loaded_row: number
    }>
  }

  const getBasePayload = (selectedVendor: FieldValues['vendors'][0]): Payload => ({
    vendor_id: Number(selectedVendor?.vendor_id) || 0,
    sourceName: selectedVendor.source_name,
    sourceType: translateSourceTypeToNumber,
    schedule_data: {
      fetch_frequency: selectedVendor?.fetch_frequency?.value ?? undefined,
      fetch_day: selectedVendor?.fetch_day?.value ?? undefined,
      fetch_time: selectedVendor?.fetch_time?.value ?? undefined,
      fetch_timezone: selectedVendor?.fetch_timezone ?? undefined,
    },
  })

  const getCDKPayload = (): Partial<Payload> => ({
    config_data: {
      cdk_api_type: sourceType === 'sales' ? 'sale' : 'service',
    },
  })
  const getDealerSocketPayload = (): Partial<Payload> => ({
    config_data: {
      dealer_id: dealership?.dealersocket_dealership_id,
      date_from: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
      file_type: sourceType,
    },
  })

  const getDealerVaultPayload = (values): Partial<Payload> => ({
    config_data:
      user?.role_id == 1
        ? {
            dv_dealer_id: values.dv_dealer_id,
            dv_file_type: sourceType === 'sales' ? 'SL' : 'SV',
          }
        : {
            dealer_vault_setup_info: { ...values },
          },
  })

  const getDriveCentricPayload = (): Partial<Payload> => ({
    config_data: {
      dealer_id: dealership?.drivecentric_dealership_id,
      file_type: sourceType,
    },
  })

  const getEleadPayload = (values): Partial<Payload> => ({
    config_data: {
      date_from: values?.date_from ?? '',
    },
  })

  const getFacebookLeadsPayload = (values): Partial<Payload> => ({
    accounts: [
      {
        account_id: values.id,
        rowsMapping: values.rowsMapping,
      },
    ],
  })

  const getGoogleSpreadSheetsPayload = (values): Partial<Payload> => ({
    spreadsheets: [
      {
        spreadsheet_id: values.id,
        rowsMapping: values.rowsMapping,
        last_loaded_row: 0,
      },
    ],
  })

  const getTekionPayload = (): Partial<Payload> => ({
    config_data: {
      dealer_id: dealership?.tekion_dealership_id,
      product: sourceType,
    },
  })

  const getVinSolutionsPayload = (): Partial<Payload> => ({
    config_data: {
      date_from: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
    },
  })

  const getGoogleLeadsPayload = (values): Partial<Payload> => ({
    accounts: [values.account.value],
  })

  const getProcedePayload = (): Partial<Payload> => ({
    config_data: {
      dealer_id: dealership?.procede_dealership_id?.value,
      file_type: sourceType,
    },
  })

  const getPromaxPayload = (): Partial<Payload> => ({
    config_data: {
      dealer_id: dealership?.promax_dealership_id?.value,
      file_type: sourceType,
    },
  })

  const getHubSpotPayload = (values): Partial<Payload> => ({})
  const getLaunchLabsPayload = (values): Partial<Payload> => ({})

  const onSubmit = async (values: FieldValues): Promise<void> => {
    const toastId = toast.loading('Importing customers')
    try {
      const { vendors } = values
      const selectedVendor = vendors?.[0]

      const customVendorPayload = match(Number(selectedVendor?.vendor_id))
        .with(VENDOR.CDK, () => getCDKPayload())
        .with(VENDOR.DealerSocket, () => getDealerSocketPayload())
        .with(VENDOR.DealerVault, () => getDealerVaultPayload(selectedVendor))
        .with(VENDOR.DriveCentric, () => getDriveCentricPayload())
        .with(VENDOR.Elead, () => getEleadPayload(selectedVendor))
        .with(VENDOR.Procede, () => getProcedePayload())
        .with(VENDOR.FacebookLeads, () => getFacebookLeadsPayload(selectedVendor))
        .with(VENDOR.GoogleLeads, () => getGoogleLeadsPayload(selectedVendor))
        .with(VENDOR.GoogleSpreadSheets, () =>
          getGoogleSpreadSheetsPayload(selectedVendor),
        )
        .with(VENDOR.Tekion, () => getTekionPayload())
        .with(VENDOR.VinSolutions, () => getVinSolutionsPayload())
        .with(VENDOR.Promax, () => getPromaxPayload())
        .with(VENDOR.HubSpot, () => getHubSpotPayload(selectedVendor))
        .with(VENDOR.LaunchLabs, () => getLaunchLabsPayload(selectedVendor))
        .otherwise(() => ({}))

      GA4.sendEvent({
        event_name: 'connected_source',
        event_params: vendors,
      })
      mtm.sendEvent('Connected', 'Source', undefined, dealership?.id)

      await mutateAsync({
        data: {
          ...getBasePayload(selectedVendor),
          ...customVendorPayload,
        },
      })
      toast.success('Successfully added source', { id: toastId })
      closeModal()
      onSuccess()
    } catch (err) {
      toast.error('There was an error importing your customers', {
        id: toastId,
      })
    }
  }
  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-5 font-Spartan h-screen'}>
        <h2 className="px-12 font-bold text-[28px]">Connect a 3rd party source</h2>
        <div className={'grid grid-cols-3 pt-12'}>
          <div className={'col-span-1 gap-4 flex flex-col pl-12'}>
            <div className={'font-bold text-base'}>Choose source</div>
            <div className="flex flex-row pt-0 ">
              <span className="font-bold text-sm mr-1">{capitalize(sourceType)}</span>
              <span className="text-sm">(based on your selection)</span>
            </div>
          </div>
          <div className={'col-span-2 pl-4'}>
            <div className={'font-bold text-base'}>Accounts</div>
          </div>
        </div>
        <div className={'grid grid-cols-3 pt-8 flex-1 overflow-hidden'}>
          <div className={'col-span-1 pl-12 pr-2 overflow-y-scroll border-r-2 '}>
            <div className={'pb-4 flex flex-col flex-1 gap-4'}>
              {mappedVendorsToSourceType
                .filter((f) => f.sourceType === sourceType)
                .sort((a, b) => {
                  if (a.disabled) return 1
                  if (b.disabled) return -1
                  return 0
                })
                .map((source, index) => {
                  if (!source) return null
                  const disabled = source.disabled

                  return (
                    <div
                      className={'flex flex-col gap-3'}
                      key={`${source.sourceType}-${index}`}>
                      {index > 0 && (
                        <div className="flex flex-row pt-0 ">
                          <span className="font-bold text-sm mr-1">
                            {capitalize(source.sourceType)}
                          </span>
                        </div>
                      )}
                      {source.vendors
                        .sort((a, b) => {
                          if (a?.connected) return -1
                          if (b?.connected) return 1
                          return 0
                        })
                        .map((vendor) => {
                          if (!vendor) return null
                          return (
                            <div
                              key={`${source.sourceType}-${vendor.id}`}
                              className={'flex items-center justify-between'}>
                              <div className={'flex items-center gap-3 flex-1'}>
                                <div className={'flex items-center justify-center'}>
                                  <div
                                    className={
                                      'w-8 h-8 rounded-full flex justify-center'
                                    }>
                                    <img
                                      src={getImageURL(`images/vendors/${vendor.logo}`)}
                                      className={'object-contain'}
                                    />
                                  </div>
                                </div>
                                <span
                                  className={`font-bold text-base text-blue-primary flex-1 ${
                                    !vendor.connected || disabled ? 'opacity-50' : ''
                                  }`}>
                                  {vendor.name}
                                </span>

                                {!disabled && (
                                  <Button
                                    disabled={
                                      hasVendorSelected ? !!vendor.connected : false
                                    }
                                    key={vendor.id}
                                    variant={'secondary'}
                                    animate={false}
                                    onClick={(e) => {
                                      e.preventDefault()

                                      if (!vendor.connected) {
                                        if (
                                          vendor.id === VENDOR.FacebookLeads ||
                                          vendor.id === VENDOR.GoogleLeads ||
                                          vendor.id === VENDOR.HubSpot
                                        ) {
                                          openSignInWindow(
                                            `${import.meta.env.VITE_API_URL}${String(
                                              vendor.api_oauth_endpoint,
                                            )}?current_organization_id=${
                                              dealership?.organization_id
                                            }&scope=${vendor.required_scopes}&vendor_id=${
                                              vendor.id
                                            }`,
                                            'test',
                                          )
                                        } else if (vendor.id === VENDOR.Elead) {
                                          handleModal(<EleadInstructions />)
                                        } else if (vendor.id === VENDOR.LaunchLabs) {
                                          handleModal(<LaunchLabsConnectInstructions />)
                                        } else if (vendor.id === VENDOR.DealerSocket) {
                                          handleModal(<DealerSocketInstructions />)
                                        } else if (vendor.id === VENDOR.CDK) {
                                          handleModal(<CDKInstructions />)
                                        } else if (vendor.id === VENDOR.VinSolutions) {
                                          handleModal(<VinSolutionsInstructions />)
                                        } else if (
                                          vendor.id === VENDOR.Tekion &&
                                          user?.role_id === 1
                                        ) {
                                          handleModal(<TekionInstructions />)
                                        } else if (
                                          vendor.id === VENDOR.DriveCentric &&
                                          user?.role_id === 1
                                        ) {
                                          handleModal(<DriveCentricInstructions />)
                                        }
                                      }
                                      if (vendor.connected) {
                                        if (vendor.id === VENDOR.FacebookLeads) {
                                          handleModal(
                                            <FacebookLeadsFileMapper
                                              databaseColumns={databaseColumns}
                                              onSave={(values) => {
                                                append({
                                                  vendor_id: vendor.id,
                                                  ...values,
                                                })
                                              }}
                                            />,
                                          )
                                        } else if (
                                          vendor.id === VENDOR.GoogleSpreadSheets
                                        ) {
                                          handleModal(
                                            <GoogleSpreadsheetsFileMapper
                                              databaseColumns={databaseColumns}
                                              onSave={(values) => {
                                                append({
                                                  vendor_id: vendor.id,
                                                  ...values,
                                                })
                                              }}
                                            />,
                                          )
                                        } else {
                                          if (!hasVendorSelected)
                                            append({ vendor_id: vendor.id })
                                        }
                                      }
                                    }}>
                                    {vendor.connected ? (
                                      <PlusIcon
                                        className={'w-4 h-6 stroke-secondary-field-drab '}
                                      />
                                    ) : (
                                      <LinkIcon
                                        className={
                                          'w-4 h-6 stroke-secondary-field-drab fill-secondary-field-drab'
                                        }
                                      />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )
                })}
            </div>
          </div>
          <div className={'col-span-2 pl-4 overflow-y-scroll'}>
            <div className={'flex flex-col flex-1 gap-4'}>
              {isEmpty(formValues) && (
                <div className="pt-0 flex flex-row">
                  <ChevronLeft className="stroke-[#828282] fill-[#828282] mr-4 " />
                  <span className="font-medium text-[#828282] text-sm">
                    Choose source to get started
                  </span>
                </div>
              )}
              {fields.map((field, index) => {
                const vendor_id = field.vendor_id
                return match(Number(vendor_id))
                  .with(VENDOR.CDK, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceCDK vendor={vendor} onRemove={removeVendor} index={index} />
                    )
                  })
                  .with(VENDOR.DealerSocket, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceDealerSocket
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.DealerVault, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceDealerVault
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                        sourceType={sourceType}
                      />
                    )
                  })
                  .with(VENDOR.DriveCentric, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceDriveCentric
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.Elead, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceElead
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.FacebookLeads, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceFacebookLeads
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.GoogleLeads, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceGoogleLeads
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.GoogleSpreadSheets, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceGoogleSpreadSheets
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.Tekion, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceTekion
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.VinSolutions, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceVinSolutions
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.Procede, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceProcede
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.Promax, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourcePromax
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.HubSpot, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceHubSpot
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .with(VENDOR.LaunchLabs, () => {
                    const vendor = vendors?.find((v) => v.id === Number(vendor_id))
                    if (!vendor) return null
                    return (
                      <SourceLaunchLabs
                        vendor={vendor}
                        onRemove={removeVendor}
                        index={index}
                      />
                    )
                  })
                  .otherwise(() => {
                    return null
                  })
              })}
            </div>
          </div>
        </div>

        <div className={'h-20 items-center flex justify-between border-t-2 py-4 px-12'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting} type={'submit'}>
            Import
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
